import axios from 'axios';
import { getStrapiBackend, initializeStrapiBackend } from './strapiBackend';

const STRAPI_API_TOKEN = process.env.REACT_APP_STRAPI_API_TOKEN || '';


/**
 * Fix relative URLs in HTML content from Strapi
 */
const fixHtmlContent = (html) => {
    if (!html || typeof html !== 'string') return html;

    // Add crossorigin="anonymous" to all img tags to prevent 431 errors
    // (Ensure we don't add it multiple times if it's already there)
    let fixedHtml = html.replace(/<img (?![^>]*crossorigin="anonymous")/g, '<img crossorigin="anonymous" ');

    // Replace all relative /uploads/ paths (in src, srcset, href, etc.) with absolute paths from Strapi
    fixedHtml = fixedHtml.replace(/(["']|,|\s)\/uploads\//g, `$1${getStrapiBackend().uploadsUrl}/uploads/`);

    return fixedHtml;
};

// Create axios instance with default config
const strapiAPI = axios.create({
    baseURL: getStrapiBackend().apiUrl,
    headers: {
        ...(STRAPI_API_TOKEN ? { Authorization: `Bearer ${STRAPI_API_TOKEN}` } : {}),
        'Content-Type': 'application/json'
    }
});

strapiAPI.interceptors.request.use(async config => {
    const backend = await initializeStrapiBackend();
    config.baseURL = backend.apiUrl;
    return config;
});

/**
 * Transform Strapi response to match Contentful structure
 */
const transformStrapiItem = (strapiItem, contentType = 'unknown') => {
    if (!strapiItem) return null;

    const { id, attributes } = strapiItem;

    // Transform nested media/images
    const transformedAttributes = {};
    Object.keys(attributes).forEach(key => {
        const value = attributes[key];

        // Handle Strapi media data structure
        // value.data can be an object (single) or array (multiple)
        if (value && value.data) {
            const isArray = Array.isArray(value.data);
            const dataItems = isArray ? value.data : [value.data];

            const transformedMedia = dataItems.map(item => {
                // Strapi 4 usually has attributes, but let's be robust
                const attrs = item?.attributes || (item?.url ? item : null);
                if (!attrs) return null;

                // Try to find the best URL (original or fallback to formats)
                let imageUrl = attrs.url;
                if (!imageUrl && attrs.formats) {
                    imageUrl = attrs.formats.large?.url || attrs.formats.medium?.url || attrs.formats.small?.url || attrs.formats.thumbnail?.url;
                }

                if (!imageUrl) {
                    return {
                        sys: { id: item.id?.toString() || '' },
                        fields: attrs
                    };
                }

                return {
                    fields: {
                        file: {
                            url: imageUrl.startsWith('http')
                                ? imageUrl
                                : `${getStrapiBackend().uploadsUrl}${imageUrl}`,
                            details: {
                                size: attrs.size,
                                image: {
                                    width: attrs.width,
                                    height: attrs.height
                                }
                            },
                            fileName: attrs.name,
                            contentType: attrs.mime
                        }
                    }
                };
            }).filter(Boolean);

            // Match Contentful structure: arrays remain arrays, single items are objects
            transformedAttributes[key] = isArray ? transformedMedia : transformedMedia[0];
        }
        // If it's not data-wrapped but looks like a media object directly (e.g. from some plugins)
        else if (value && (value.url || value.formats)) {
            const transformed = {
                fields: {
                    file: {
                        url: value.url?.startsWith('http')
                            ? value.url
                            : `${getStrapiBackend().uploadsUrl}${value.url || ''}`,
                        details: {
                            size: value.size,
                            image: { width: value.width, height: value.height }
                        },
                        fileName: value.name,
                        contentType: value.mime
                    }
                }
            };
            transformedAttributes[key] = transformed;
        }
        // Regular attributes
        else {
            transformedAttributes[key] = value;
        }
    });

    // Helper to get first defined value from potential keys
    const getVal = (keys) => {
        for (const k of keys) {
            if (attributes[k] !== undefined && attributes[k] !== null && attributes[k] !== '') {
                return attributes[k];
            }
        }
        return undefined;
    };

    // Field normalization for consistency across components
    // Publication is independent of a tender's submission deadline.
    if (attributes.publishedAt) {
        transformedAttributes['publishedAt'] = attributes.publishedAt;
    }
    if (attributes.draftAndPublish) {
        transformedAttributes['draftAndPublish'] = attributes.draftAndPublish;
    }

    // Normalize Slugs
    const normalizedSlug = getVal(['slug', 'Slug', 'slugevent', 'slugMName', 'slug_agenda', 'slug_minister', 'Slug_minister', 'Slug_Event']);
    if (normalizedSlug) transformedAttributes['slug'] = normalizedSlug;

    // Normalize Titles
    const normalizedTitle = getVal(['title', 'Title', 'menuName', 'subMName', 'name', 'directorateName', 'FullName', 'Full_Name']);
    if (normalizedTitle) transformedAttributes['title'] = normalizedTitle;

    // Normalize Descriptions/Content
    const normalizedDesc = getVal(['description', 'Description', 'content', 'contentprofile', 'contentevent', 'contentdescription', 'achievements', 'content_profile']);
    if (normalizedDesc) {
        const fixedContent = fixHtmlContent(normalizedDesc);
        transformedAttributes['description'] = fixedContent;
        // Also map back to common field names used in components to ensure they are fixed too
        transformedAttributes['contentdescription'] = fixedContent;
        transformedAttributes['contentevent'] = fixedContent;
        transformedAttributes['achievements'] = fixedContent;
        transformedAttributes['content'] = fixedContent;
    }

    // Normalize Image/Media fields
    // Many components use different names like publicationmedia, eventimage, etc.
    // We try to find any Strapi media field and map it to all expected names.
    const imageFieldAliases = [
        'publicationmedia', 'publication_media', 'PublicationMedia',
        'eventimage', 'event_image', 'EventImage',
        'profilepicture', 'profile_picture', 'ProfilePicture',
        'logopartner', 'logo_partner', 'LogoPartner',
        'logodirectorate', 'logo_directorate', 'LogoDirectorate',
        'knowledgeimage', 'knowledge_image', 'KnowledgeImage',
        'Media', 'image', 'Thumbnail', 'menupic', 'Cover', 'pic',
        'photo', 'avatar', 'Logo', 'Picture', 'file', 'Mediafile', 'media', 'cover_page'
    ];

    let mainImage = null;

    // Helper to check if a value is a valid transformed media object
    const isValidMedia = (val) => {
        return (val?.fields?.file?.url) || (Array.isArray(val) && val[0]?.fields?.file?.url);
    };

    // First, try the known aliases
    for (const alias of imageFieldAliases) {
        if (isValidMedia(transformedAttributes[alias])) {
            mainImage = transformedAttributes[alias];
            break;
        }
    }

    // If not found, scan all fields for anything that looks like a transformed media object
    if (!mainImage) {
        for (const key of Object.keys(transformedAttributes)) {
            const val = transformedAttributes[key];
            if (isValidMedia(val)) {
                mainImage = val;
                console.log(`[Strapi Debug] Found possible image field "${key}" for content type "${contentType}"`);
                break;
            }
        }
    }

    if (mainImage) {
        // Some components expect a single object (Publication, Event, etc.)
        // while others (Knowledge) expect an array.
        const isArray = Array.isArray(mainImage);
        const singleImage = isArray ? mainImage[0] : mainImage;
        const arrayImage = isArray ? mainImage : [mainImage];

        // Map to single-image fields
        const singleTargetNames = ['publicationmedia', 'eventimage', 'profilepicture', 'logopartner', 'logodirectorate'];
        singleTargetNames.forEach(name => {
            if (!transformedAttributes[name]) {
                transformedAttributes[name] = singleImage;
            }
        });

        // Map to array-image fields
        const arrayTargetNames = ['knowledgeimage'];
        arrayTargetNames.forEach(name => {
            if (!transformedAttributes[name]) {
                transformedAttributes[name] = arrayImage;
            }
        });
    }

    // Normalize Categories
    // Publications now use the shared Category collection; the old dropdown is kept as a fallback
    // until the CMS migration has run.
    const categoryName = attributes.category?.data?.attributes?.name;
    if (categoryName || attributes.publication_category) {
        transformedAttributes['publicationCategory'] = categoryName || attributes.publication_category;
    }

    // Special field mapping for sub-menus to match old menus fields
    if (contentType === 'sub-menus') {
        transformedAttributes['title'] = attributes.subMName || transformedAttributes['title'];
        transformedAttributes['slug'] = attributes.slugMName || transformedAttributes['slug'];
        transformedAttributes['menuorder'] = attributes.subMOrder || attributes.menuorder;
        transformedAttributes['description'] = transformedAttributes['content'] || transformedAttributes['description'] || (attributes.content ? fixHtmlContent(attributes.content) : undefined);
        transformedAttributes['mediafile'] = transformedAttributes.menupic || transformedAttributes.mediafile;
    }

    return {
        sys: {
            id: id.toString(),
            createdAt: attributes.createdAt,
            updatedAt: attributes.updatedAt,
            contentType: {
                sys: {
                    id: contentType
                }
            }
        },
        fields: transformedAttributes
    };
};

/**
 * Strapi client with Contentful-compatible interface
 */
const strapiClient = {
    /**
     * Get multiple entries
     * @param {Object} options - Query options
     * @param {string} options.content_type - Content type to query
     * @param {string} options.query - Search query
     * @param {number} options.limit - Number of items to return
     * @param {number} options.skip - Number of items to skip
     * @param {Object} options.order - Sort order
     */
    async getEntries(options = {}) {
        try {
            let { content_type, query, limit = 100, skip = 0, order, where, ...filters } = options;

            if (!content_type) {
                throw new Error('content_type is required');
            }

            // Handle camelCase and pluralization
            if (content_type === 'menus') {
                content_type = 'sub-menus';
            } else if (content_type === 'collectionpagedetail') {
                content_type = 'collectionpagedetails';
            } else if (/^[a-z]+[A-Z]/.test(content_type)) {
                content_type = content_type.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
            }

            // Pluralize common singular names that might be used
            const pluralMap = {
                'publication': 'publications',
                'event': 'events',
                'tender': 'tenders',
                'knowlege-center': 'knowlege-centers',
                'former-minister': 'former-ministers',
                'infographic': 'expenditure-revenues',
                'infographics': 'expenditure-revenues'
            };

            if (pluralMap[content_type]) {
                content_type = pluralMap[content_type];
            }

            // Build query parameters
            const params = {
                'pagination[limit]': limit,
                'pagination[start]': skip,
                'pagination[withCount]': true,
                'populate[0]': '*',
                // Explicitly populate common media fields to be safe
                'populate[1]': 'publicationmedia',
                'populate[2]': 'publication_media',
                'populate[3]': 'eventimage',
                'populate[4]': 'event_image',
                'populate[5]': 'profilepicture',
                'populate[6]': 'profile_picture',
                'populate[7]': 'logopartner',
                'populate[8]': 'logodirectorate',
                'populate[9]': 'knowledgeimage',
                'populate[10]': 'Thumbnail',
                'populate[11]': 'Media',
                'populate[12]': 'image',
                'populate[13]': 'pic',
                'populate[14]': 'Cover',
                'populate[15]': 'menupic',
                'populate[16]': 'media',
                'populate[17]': 'cover_page',
                'populate[18]': 'sub_menu',
                'populate[19]': 'main_menu'
            };

            // Native Strapi filters for server-side collection pagination.
            if (where) params.filters = where;
            if (content_type === 'tenders') {
                Object.keys(params).filter(key => key.startsWith('populate[')).forEach(key => delete params[key]);
            }

            // Add search if query provided
            if (query) {
                params['filters[$or][0][title][$containsi]'] = query;
                params['filters[$or][1][description][$containsi]'] = query;
            }

            // Handle additional filters (e.g., 'fields.type': 'value')
            Object.keys(filters).forEach(key => {
                let filterKey = key.startsWith('fields.') ? key.substring(7) : key;

                // Handle special filter mapping for sub-menus
                if (content_type === 'sub-menus') {
                    if (filterKey === 'type') {
                        // Map old 'type' to main_menu relation
                        params[`filters[main_menu][slug][$eq]`] = filters[key];
                        return;
                    }
                }

                // Automatic slug field mapping based on content type
                if (filterKey === 'slug') {
                    if (content_type === 'publications') filterKey = 'Slug';
                    else if (content_type === 'events') filterKey = 'slugevent';
                    else if (content_type === 'sub-menus') filterKey = 'slugMName';
                    else if (content_type === 'mo-f-agendas') filterKey = 'slug_agenda';
                }

                // Handle nested filters like 'main_menu.slug'
                if (filterKey.includes('.')) {
                    const parts = filterKey.split('.');
                    let strapiFilter = `filters`;
                    parts.forEach(part => {
                        strapiFilter += `[${part}]`;
                    });
                    params[`${strapiFilter}[$eq]`] = filters[key];
                } else {
                    params[`filters[${filterKey}][$eq]`] = filters[key];
                }
            });

            // Add sorting
            if (order) {
                let orderField = order.startsWith('-') ? order.substring(1) : order;
                if (orderField.startsWith('fields.')) orderField = orderField.substring(7);
                if (orderField === 'sys.createdAt') orderField = 'createdAt';
                if (orderField === 'sys.updatedAt') orderField = 'updatedAt';

                // Mapping for sub-menus sorting
                if (content_type === 'sub-menus' && orderField === 'menuorder') {
                    orderField = 'subMOrder';
                }

                const orderDirection = order.startsWith('-') ? 'desc' : 'asc';
                params['sort[0]'] = `${orderField}:${orderDirection}`;
                // Keep offset pagination deterministic when timestamps match.
                if (orderField !== 'id') params['sort[1]'] = 'id:asc';
            }

            const response = await strapiAPI.get(`/${content_type}`, { params });

            // Transform response to match Contentful structure
            const items = response.data.data.map(item => transformStrapiItem(item, content_type));

            return {
                items,
                total: response.data.meta?.pagination?.total,
                skip,
                limit
            };
        } catch (error) {
            console.error('Error fetching entries from Strapi:', error);
            throw error;
        }
    },

    /**
     * Get a single entry by ID
     * @param {string} id - Entry ID
     * @param {Object} options - Query options
     * @param {string} options.content_type - Content type
     */
    async getEntry(id, options = {}) {
        try {
            const { content_type } = options;

            if (!content_type) {
                throw new Error('content_type is required');
            }

            const params = {
                'populate[0]': '*',
                'populate[1]': 'publicationmedia',
                'populate[2]': 'publication_media',
                'populate[3]': 'eventimage',
                'populate[4]': 'event_image',
                'populate[5]': 'profilepicture',
                'populate[6]': 'profile_picture',
                'populate[7]': 'logopartner',
                'populate[8]': 'logodirectorate',
                'populate[9]': 'knowledgeimage',
                'populate[10]': 'Thumbnail',
                'populate[11]': 'Media',
                'populate[12]': 'image',
                'populate[13]': 'pic',
                'populate[14]': 'Cover',
                'populate[15]': 'menupic',
                'populate[16]': 'media'
            };

            const response = await strapiAPI.get(`/${content_type}/${id}`, { params });

            return transformStrapiItem(response.data.data, content_type);
        } catch (error) {
            console.error('Error fetching entry from Strapi:', error);
            throw error;
        }
    },

    /**
     * Get entries by field value
     * @param {string} contentType - Content type to query
     * @param {string} field - Field name to filter by
     * @param {*} value - Field value to match
     */
    async getEntriesByField(contentType, field, value) {
        try {
            const params = {
                'populate': '*',
                [`filters[${field}][$eq]`]: value
            };

            const response = await strapiAPI.get(`/${contentType}`, { params });

            const items = response.data.data.map(item => transformStrapiItem(item, contentType));

            return {
                items,
                total: response.data.meta?.pagination?.total || items.length
            };
        } catch (error) {
            console.error('Error fetching entries by field from Strapi:', error);
            throw error;
        }
    }
};

export default strapiClient;
